// SitrepGenerationProgress.jsx — MINSKY-16410: the under-the-map-embed progress/result UI for
// one situation-report generation request. Driven purely by the `phase` derived via
// sitrepProgressState.getSitrepProgressViewState (see that module) — this component only
// renders, it doesn't touch Yjs itself; MapNodeView owns the observer/subscription and passes
// the derived view-state + a couple of callbacks down as props (mirrors how AgenticSectionControls
// is a pure render of state MapNodeView-equivalent orchestration computes upstream).
//
// Placement/shape: rendered by MapNodeChrome directly under the map container (see
// MapNodeChrome._build's mapContainer/captionEl ordering) as a plain block-level element, not
// absolute-positioned over the map — unlike the existing `_queueBadge` overlay (small pill
// anchored bottom-right over the snapshot), the user explicitly asked for a full-width **linear**
// bar "under the map", i.e. its own row in the chrome's flow layout, so this is a new small
// component rather than a restyle of the circular/overlay queue badge.

import React, {useEffect} from 'react';
import {Box, Button, IconButton, LinearProgress, Stack, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {SITREP_PROGRESS_PHASE} from './sitrepProgressState';

// MINSKY-16410 point 3: "brief, non-intrusive" — long enough to read a short sentence, short
// enough not to linger. No existing sitrep-specific precedent for this duration; picked to match
// the Snackbar `autoHideDuration`s already used elsewhere in this app (MessageComposer.jsx uses
// 5000, DocumentContextMenus.jsx uses 6000) rather than inventing a third value.
const SUCCESS_AUTO_DISMISS_MS = 6000;

export function SitrepGenerationProgress({
    phase = SITREP_PROGRESS_PHASE.IDLE,
    label = '',
    errorMessage = '',
    onRetry,
    retryPending = false,
    onDismissSuccess,
}) {
    const isActive = phase === SITREP_PROGRESS_PHASE.QUEUED || phase === SITREP_PROGRESS_PHASE.GENERATING;
    const isDone = phase === SITREP_PROGRESS_PHASE.DONE;
    const isError = phase === SITREP_PROGRESS_PHASE.ERROR;

    useEffect(() => {
        if (!isDone || typeof onDismissSuccess !== 'function') return undefined;
        const timeoutId = setTimeout(onDismissSuccess, SUCCESS_AUTO_DISMISS_MS);
        return () => clearTimeout(timeoutId);
    }, [isDone, onDismissSuccess]);

    if (!isActive && !isDone && !isError) return null;

    return (
        <Box
            className="pm-map-node__sitrep-progress"
            data-testid="pm-map-node-sitrep-progress"
            sx={{px: 0.5, pt: 0.5}}
        >
            {isActive ? (
                <Stack spacing={0.5}>
                    <LinearProgress aria-label="Generating situation report" />
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Stack>
            ) : null}

            {isDone ? (
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="success.main">
                        Suggestion added — review it in the document.
                    </Typography>
                    {typeof onDismissSuccess === 'function' ? (
                        <IconButton
                            size="small"
                            onClick={onDismissSuccess}
                            aria-label="Dismiss"
                            data-testid="pm-map-node-sitrep-progress-dismiss"
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    ) : null}
                </Stack>
            ) : null}

            {isError ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography
                        variant="caption"
                        color="error"
                        data-testid="pm-map-node-sitrep-progress-error"
                    >
                        {errorMessage || 'Failed to generate situation report.'}
                    </Typography>
                    <Button
                        size="small"
                        onClick={onRetry}
                        disabled={retryPending}
                        data-testid="pm-map-node-sitrep-progress-retry"
                    >
                        Retry
                    </Button>
                </Stack>
            ) : null}
        </Box>
    );
}
